import React from "react";
import {
  Table,
  type TableColumn,
  Skeleton,
  EmptyState,
  Pagination,
  VStack,
  HStack,
} from "@astryxdesign/core";

export interface DataTableEmptyState {
  icon?: React.ReactNode;
  headline: string;
  subtext?: string;
  action?: React.ReactNode;
}

export interface DataTablePagination {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

export interface DataTableProps<T extends Record<string, unknown>> {
  loading: boolean;
  data: T[];
  columns: TableColumn<T>[];
  /**
   * Row key for React reconciliation — a property name on `T` (e.g. `"id"`)
   * or a custom extractor function. Passed straight through to Astryx Table.
   */
  idKey: (keyof T & string) | ((item: T) => string | number);
  emptyState?: DataTableEmptyState;
  pagination?: DataTablePagination;
  skeletonRows?: number;
}

/**
 * Loadable, paginated wrapper around Astryx's data-driven `Table`.
 *
 * Sibling of `LoadableCardGrid`: same `loading`/`emptyState` contract, but
 * for tabular data instead of a card grid. Composes `Skeleton` for the
 * loading state, `EmptyState` for the empty state, and `Pagination` for
 * paging — callers only need to supply `data`, `columns`, and `idKey`.
 */
const DataTable = <T extends Record<string, unknown>>({
  loading,
  data,
  columns,
  idKey,
  emptyState,
  pagination,
  skeletonRows = 5,
}: DataTableProps<T>): React.ReactElement => {
  if (loading) {
    return (
      <VStack gap={2}>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <Skeleton key={i} width="100%" height={40} index={i} />
        ))}
      </VStack>
    );
  }

  // Done loading with nothing to show — render the design-system empty state.
  if (emptyState && data.length === 0) {
    return (
      <EmptyState
        title={emptyState.headline}
        description={emptyState.subtext}
        icon={emptyState.icon}
        actions={emptyState.action}
      />
    );
  }

  return (
    <VStack gap={4}>
      <Table data={data} columns={columns} idKey={idKey} dividers="rows" hasHover />

      {/* Pagination's own root is space-between, so the compact variant's lone
          control cluster would sit at the start; this trails it instead. */}
      {pagination && pagination.totalPages > 1 && (
        <HStack justify="end">
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            onChange={pagination.onChange}
            variant="compact"
          />
        </HStack>
      )}
    </VStack>
  );
};

export default DataTable;
